import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { RouteError } from './components/shared/route-error';
import { RoutePending } from './components/shared/route-pending';
import { loadTranslations, type SupportedLanguage } from './lib/i18n';
import './app/globals.css';

// Create the router instance
const router = createRouter({
  routeTree,
  defaultPendingComponent: RoutePending,
  // Load lazy route chunks while a merchant is indicating intent (hover,
  // keyboard focus, or touch) so primary navigation is ready before click.
  defaultPreload: 'intent',
  defaultPreloadDelay: 60,
  // Give intent preloading a short head start, then replace stale route
  // content quickly with a destination-shaped skeleton when a chunk is slow.
  defaultPendingMs: 180,
  defaultPendingMinMs: 160,
  scrollRestoration: true,
  // Route-scoped error fallback: a thrown page error (or a lazy-chunk load
  // failure after a deploy) is contained to the page area, keeping the app
  // shell usable instead of blanking the whole dashboard.
  defaultErrorComponent: RouteError,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function getInitialLanguage(): SupportedLanguage {
  return document.documentElement.lang === 'en' ? 'en' : 'ar';
}

async function bootstrap() {
  const language = getInitialLanguage();
  try {
    await loadTranslations(language);
  } catch (error) {
    console.error(`Failed to load the ${language} dashboard locale`, error);
    if (language !== 'en') {
      await loadTranslations('en');
      document.documentElement.lang = 'en';
      document.documentElement.dir = 'ltr';
    }
  }

  const rootElement = document.getElementById('root')!;
  createRoot(rootElement).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

void bootstrap();
